import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Tabs, TabsProps } from 'antd/es';

import AreaPageMobileNav from '@app/components/Pages/Area/AreaPageMobileNav';
import AreaAbout from '@app/components/UI/Area-blocks/AreaAbout/AreaAbout';
import AreaMemorialKeepsakes from '@app/components/UI/Area-blocks/AreaMemorialKeepsakes/AreaMemorialKeepsakes';
import AreaPreview from '@app/components/UI/Area-blocks/AreaPreview/AreaPreview';
import AreaRequestForm from '@app/components/UI/Area-blocks/AreaRequestForm/AreaRequestForm';
import AreaResources from '@app/components/UI/Area-blocks/AreaResources/AreaResources';
import AreaServices from '@app/components/UI/Area-blocks/AreaServices/AreaServices';
import AreaVets from '@app/components/UI/Area-blocks/AreaVets/AreaVets';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import HelmetProvider from '@app/providers/HelmetProvider/HelmetProvider';
import MastheadNaked from '@app/components/UI/MastHead/MastheadNaked';
import { useIsMobile } from '@app/hooks/useIsMobile';
import tampaStPetersburg from '@app/images/content/tampa-st-petersburg.jpg';
import { Area } from '@app/objects/Area';
import '@app/scss/components/area.scss';
import '@app/scss/components/middleText.scss';

export const AreaPageContext = React.createContext<string>('');

interface Params extends Record<string, string> {
	area: string;
	id: string;
	tab: string;
}

const AreaPageContent: React.FC<{ area: Area; params: Params; }> = ({ area, params }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const currentAreaUrl: string = location.pathname.slice(0, -params.tab.length - 1);

	const isMobile = useIsMobile();

	const tabs: TabsProps['items'] = [
		{
			key: 'about',
			label: 'About',
			children: <AreaAbout area={area} />,
		},
		...(!area?.hidePricingSection ? [{
			key: 'services',
			label: 'Pricing & Services',
			children: <AreaServices area={area} />,
		}] : []),
		{
			key: 'veterinarians',
			label: 'Veterinarians',
			children: <AreaVets area={area} />,
		},
		...(!area?.hideResourcesSection ? [{
			key: 'resources',
			label: 'Local and Emergency Resources',
			children: <AreaResources area={area} />,
		}] : []),
		...(!area.hideMemorialKeepsakesSection ? [{
			key: 'keepsakes',
			label: 'Pet Memorial Keepsakes',
			children: <AreaMemorialKeepsakes area={area} />,
		}] : []),
		{
			key: 'request-info',
			label: 'Request info',
			children: <AreaRequestForm area={area} />,
		},
	];

	const scrollTop = () => {
		window.scrollTo(0, 0);
		document.querySelector('#react-app')?.scrollTo(0, 0);
	};

	React.useEffect(() => {
		scrollTop();
	}, []);

	React.useEffect(() => {
		if (isMobile) {
			scrollTop();
		}
	}, [location.pathname]);

	return (
		<AreaPageContext.Provider value={currentAreaUrl}>
			{(area.title || area.name) && (
				<Helmet>
					<title>{area.title || `${area.name} | Lap of Love`}</title>
					<meta property="og:title" content={area.title || `${area.name} | Lap of Love`} />
					<meta name="twitter:title" content={area.title || `${area.name} | Lap of Love`} />
					<meta name="description" content={area.description || undefined} />
					<meta property="og:description" content={area.description || undefined} />
					<meta name="twitter:description" content={area.description || undefined} />
				</Helmet>
			)}
			<AreaPageMobileNav currentAreaUrl={currentAreaUrl} activeTab={params.tab} additionalClass="area-page__mobile-nav" />
			<div className="area-page">
				<MastheadNaked imageSrc={area.headerImage || tampaStPetersburg} />
				<div className="area-page__avatar area-page__avatar_mobile">
					<CustomAvatar
						src={area.avatar}
						maxWidth={250}
						maxHeight={250}
					/>
				</div>
				<div className="area-page-body">
					<Tabs
						className="area-page-menu"
						activeKey={params.tab}
						tabPosition="left"
						tabBarExtraContent={<AreaPreview area={area} currentAreaUrl={currentAreaUrl} />}
						onTabClick={(key, event) => {
							navigate(`${currentAreaUrl}/${key}`);
						}}
						items={tabs}
					/>
				</div>
			</div>
		</AreaPageContext.Provider>
	);
};

const AreaPage: React.FC = () => {
	const params = useParams<Params>();

	return (
		<HelmetProvider>
			{(page: any) => (
				<AreaPageContent
					area={page.item}
					params={params as Params}
				/>
			)}
		</HelmetProvider>
	);
};

export default AreaPage;
